import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Instagram, MapPin } from 'lucide-react';
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.22, 1, 0.36, 1] // Custom smooth ease-out
    }
  }
};
const INSTAGRAM_URL = 'https://www.instagram.com/lilla.verket/';

// Uppdatera adressen här. Både gatuadress och orten hjälper kartappen hitta rätt.
const ADDRESS_LABEL = 'Lilla Verket';
const ADDRESS_QUERY = 'Lilla Verket, Sverige';

// Uppdatera öppettiderna här — de speglar informationen i Instagram-bion.
const OPENING_HOURS = [{
  days: 'Ons–Sön',
  time: '08:00–16:00'
}];

// Väljer rätt kartapp utifrån besökarens enhet:
// Apple-enheter (iPhone/iPad/Mac) → Apple Maps, övriga → Google Maps.
function buildMapsUrl(): string {
  const query = encodeURIComponent(ADDRESS_QUERY);
  if (typeof navigator === 'undefined') {
    return `https://www.google.com/maps/search/?api=1&query=${query}`;
  }
  const ua = navigator.userAgent || '';
  const isApple = /iPhone|iPad|iPod|Macintosh/.test(ua) || navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;
  return isApple ? `https://maps.apple.com/?q=${query}` : `https://www.google.com/maps/search/?api=1&query=${query}`;
}
export function HeroSection() {
  // Google Maps som säker standard tills enheten hunnit identifieras i webbläsaren.
  const [mapsUrl, setMapsUrl] = useState(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS_QUERY)}`);
  useEffect(() => {
    setMapsUrl(buildMapsUrl());
  }, []);
  return <section className="flex flex-col items-center justify-center min-h-screen px-6 py-12 md:py-24 max-w-3xl mx-auto text-center" data-id="element-3" data-mp="1dqomx3v06wqi">
      <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-col items-center w-full" data-id="element-4" data-mp="18tg4r39uusp9">
        {/* Logo */}
        <motion.div variants={itemVariants} className="mb-12 md:mb-16" data-id="element-5" data-mp="3duihppegb3u4">
          <img src="https://cdn.magicpatterns.com/uploads/ao2DrHXUU5TPkmLZkMQLjL/Namnlos_(Instagram-inlagg_(45)).png" alt="Lilla Verket Logo" className="w-72 md:w-96 h-auto" draggable={false} data-id="element-6" data-mp="3iqplbbjdk38w" />
        </motion.div>

        {/* Tagline */}
        <motion.div variants={itemVariants} className="mb-10" data-id="element-7" data-mp="kl21dh3iomj">
          <h1 className="font-serif text-sm md:text-base tracking-[0.25em] uppercase text-ink/80" data-id="element-8" data-mp="5c2tnax8bcuu">
            Bageri <span className="mx-2 opacity-50" data-id="element-9" data-mp="abyxna5xf4ae">•</span>
            Café <span className="mx-2 opacity-50" data-id="element-10" data-mp="adiep2zepjd8">•</span>
            Vin <span className="mx-2 opacity-50" data-id="element-11" data-mp="eec646c25gav">•</span>
            Ost & Chark
          </h1>
        </motion.div>

        {/* Divider */}
        <motion.div variants={itemVariants} className="mb-10" data-id="element-12" data-mp="jaaz678bzmhm">
          <div className="w-12 h-[1px] bg-ink/20" data-id="element-13" data-mp="23v7n5rpc1dxu" />
        </motion.div>

        {/* Announcement */}
        <motion.div variants={itemVariants} className="mb-12" data-id="element-14" data-mp="1yujyhcokt96s">
          <h2 className="font-serif italic text-2xl md:text-3xl lg:text-4xl text-ink font-light tracking-wide" data-id="element-15" data-mp="1ut846turbzs9">
            Nya verk, nya tider
          </h2>
        </motion.div>

        {/* Hours */}
        <motion.div variants={itemVariants} className="mb-16" data-id="element-16" data-mp="2mlfl7ve6de76">
          <div className="inline-flex flex-col items-center gap-3 px-8 py-5 border border-ink/15 rounded-full" data-id="element-17" data-mp="2mn0ivtvsdd99">
            <p className="font-sans text-[10px] md:text-xs tracking-[0.25em] uppercase font-medium text-ink/80" data-id="element-18" data-mp="2dsm3lrb9a2q5">
              Semesteröppettider
            </p>
            <div className="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-4 font-sans text-[11px] md:text-xs tracking-[0.15em] uppercase text-ink/70" data-id="element-19" data-mp="28szofcbvacep">
              {OPENING_HOURS.map((entry, index) => <React.Fragment key={entry.days} data-id="element-20" data-mp="31gbywo4r6trn" data-mpi={index}>
                  <div className="flex items-center gap-2" data-id="element-21" data-mp="2l1maoi4cuzqo">
                    <span className="font-medium text-ink/90" data-id="element-22" data-mp="2q1qscbh020ih">{entry.days}</span>
                    <span data-id="element-23" data-mp="2pze3rfgqkrwg">{entry.time}</span>
                  </div>
                  {index < OPENING_HOURS.length - 1 && <span className="hidden md:block opacity-30" data-id="element-24" data-mp="2h0ss849omd56">•</span>}
                </React.Fragment>)}
            </div>
          </div>
        </motion.div>

        {/* Location */}
        <motion.div variants={itemVariants} className="mb-12" data-id="element-25" data-mp="38si1pd0zlqwv">
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2 font-sans text-[11px] md:text-xs tracking-[0.2em] uppercase text-ink/60 hover:text-ink transition-colors duration-300" aria-label={`Hitta hit till ${ADDRESS_LABEL} — öppnas i din kartapp`} data-id="element-26" data-mp="2v0v499vb6cci">
            <MapPin strokeWidth={1.5} className="w-3.5 h-3.5" data-id="element-27" data-mp="3sibxs3aqis6w" />
            <span className="border-b border-transparent group-hover:border-ink/40 transition-colors duration-300" data-id="element-28" data-mp="5olo8wt9v5fv">
              Hitta hit
            </span>
          </a>
        </motion.div>

        {/* Social Link */}
        <motion.div variants={itemVariants} className="mt-4" data-id="element-29" data-mp="feonvfuwfhn6">
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-2.5 py-2 text-ink/50 hover:text-ink transition-colors duration-300" aria-label="Följ Lilla Verket på Instagram" data-id="element-30" data-mp="ag3o1hjk6zz9">
            <Instagram strokeWidth={1.5} className="w-5 h-5" data-id="element-31" data-mp="3sve6q3txo77j" />
            <span className="font-sans text-[11px] md:text-xs tracking-[0.15em] lowercase" data-id="element-32" data-mp="3nv9pucp45ags">
              @lilla.verket
            </span>
          </a>
        </motion.div>
      </motion.div>
    </section>;
}